import type { AgendaSchedule, Job, Technician } from '@/types'

export type AgendaJobPatch = {
  scheduled_date?: string
  start_time?: string | null
  end_time?: string | null
  technician_ids?: string[]
}

function dateInRange(
  date: string | null,
  range: AgendaSchedule['range']
): boolean {
  if (!date) return false
  const isoDate = date.slice(0, 10)
  return isoDate >= range.from && isoDate <= range.to
}

function patchedJob(
  job: Job,
  patch: AgendaJobPatch,
  assignedTechnicians?: Technician[]
): Job {
  let status = job.status
  if (patch.technician_ids !== undefined) {
    if (status === 'to_plan' && patch.scheduled_date) {
      status = patch.technician_ids.length > 0 ? 'assigned' : 'planned'
    } else if (status === 'planned' && patch.technician_ids.length > 0) {
      status = 'assigned'
    } else if (status === 'assigned' && patch.technician_ids.length === 0) {
      status = 'planned'
    }
  }

  return {
    ...job,
    status,
    scheduled_date: patch.scheduled_date ?? job.scheduled_date,
    start_time:
      patch.start_time === undefined ? job.start_time : patch.start_time,
    end_time: patch.end_time === undefined ? job.end_time : patch.end_time,
    assigned_technicians: assignedTechnicians ?? job.assigned_technicians,
    callback_date: status === 'to_plan' ? job.callback_date : null,
    callback_time: status === 'to_plan' ? job.callback_time : null,
  }
}

export function applyAgendaJobSchedulePatch(
  schedule: AgendaSchedule,
  jobId: string,
  patch: AgendaJobPatch,
  fallbackJob?: Job | null,
  knownTechnicians: Technician[] = []
): AgendaSchedule {
  let sourceJob: Job | null = fallbackJob ?? null
  const originalTechnicianIds = new Set<string>()
  const techniciansById = new Map<string, Technician>()
  const existingTechnicianIds = new Set<string>()

  for (const technician of knownTechnicians) {
    techniciansById.set(technician.id, technician)
  }

  for (const row of schedule.technicians) {
    techniciansById.set(row.technician.id, row.technician)
    existingTechnicianIds.add(row.technician.id)

    if (row.jobs.some(job => job.id === jobId)) {
      originalTechnicianIds.add(row.technician.id)
      sourceJob ??= row.jobs.find(job => job.id === jobId) ?? null
    }
  }

  if (!sourceJob) return schedule

  const nextDate = patch.scheduled_date ?? sourceJob.scheduled_date
  const shouldShowInThisSchedule = dateInRange(nextDate, schedule.range)
  const targetTechnicianIds =
    patch.technician_ids === undefined
      ? originalTechnicianIds
      : new Set(patch.technician_ids)
  const assignedTechnicians =
    patch.technician_ids === undefined
      ? undefined
      : patch.technician_ids
          .map(id => techniciansById.get(id))
          .filter((technician): technician is Technician => Boolean(technician))
  const nextJob = patchedJob(sourceJob, patch, assignedTechnicians)
  const patchedTechnicians = schedule.technicians.map(row => {
    const jobsWithoutTarget = row.jobs.filter(job => job.id !== jobId)
    const shouldInsert =
      shouldShowInThisSchedule && targetTechnicianIds.has(row.technician.id)

    return {
      ...row,
      jobs: shouldInsert ? [...jobsWithoutTarget, nextJob] : jobsWithoutTarget,
    }
  })

  if (shouldShowInThisSchedule) {
    for (const technicianId of targetTechnicianIds) {
      if (existingTechnicianIds.has(technicianId)) continue
      const technician = techniciansById.get(technicianId)
      if (!technician) continue

      existingTechnicianIds.add(technicianId)
      patchedTechnicians.push({
        technician,
        jobs: [nextJob],
      })
    }
  }

  return {
    ...schedule,
    technicians: patchedTechnicians,
  }
}
